import type { Holding } from './api-types'
import { BNB_ID, CORE_TOKEN_LOGOS, ETH_ID, SOL_MINT, USDC_MINT, USDT_MINT } from './default-tokens'

/**
 * The chains the exchanges move the account's coins on (Nov 3 2026, John:
 * "the CEX is chain agnostic, it's just the deposit/withdraw stuff that
 * matters for chain"). A balance has no chain; a deposit address and a
 * payout do. Spelled like the server's ExchangeNetwork.
 */
export type Network = 'solana' | 'ethereum' | 'base' | 'bsc' | 'robinhood'
export const NETWORKS: readonly Network[] = ['solana', 'ethereum', 'base', 'bsc', 'robinhood']
export const NETWORK_LABELS: Record<Network, string> = { solana: 'Solana', ethereum: 'Ethereum', base: 'Base', bsc: 'BNB Smart Chain', robinhood: 'Robinhood Chain' }
/** the chains' marks, served by us like the coin logos; Robinhood Chain's is the feather on lime from robinhood.com's brand kit (Nov 4 2026) */
export const NETWORK_LOGOS: Record<Network, string> = {
  solana: '/brand/tokens/sol.png',
  ethereum: '/brand/tokens/eth.svg',
  base: '/brand/tokens/base.png',
  bsc: '/brand/tokens/bnb.png',
  robinhood: '/brand/tokens/robinhood.svg',
}
/** the chain as one word beside a ticker ("ETH Base", "BNB BSC"), where the full name would not fit */
export const NETWORK_SHORT_LABELS: Record<Network, string> = { solana: 'Solana', ethereum: 'Ethereum', base: 'Base', bsc: 'BSC', robinhood: 'Robinhood' }

/** the coins the exchanges hold for the account, by the "mint" their rows carry */
export type ExchangeCoin = 'SOL' | 'USDC' | 'USDT' | 'ETH' | 'BNB'
export const EXCHANGE_COINS: readonly ExchangeCoin[] = ['SOL', 'USDC', 'USDT', 'ETH', 'BNB']
export const EXCHANGE_COIN_NAMES: Record<ExchangeCoin, string> = { SOL: 'Solana', USDC: 'USD Coin', USDT: 'Tether USD', ETH: 'Ethereum', BNB: 'BNB' }
export const EXCHANGE_COIN_DECIMALS: Record<ExchangeCoin, number> = { SOL: 9, USDC: 6, USDT: 6, ETH: 18, BNB: 18 }
/** the row key (the Solana mint, or the pseudo-mint) each exchange coin's records carry */
export const EXCHANGE_COIN_IDS: Record<ExchangeCoin, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT, ETH: ETH_ID, BNB: BNB_ID }
/** the networks each coin moves on; the first is its own */
export const COIN_NETWORKS: Record<ExchangeCoin, readonly Network[]> = {
  SOL: ['solana'],
  // the EVM chains the exchanges rail the stablecoins on (John, Nov 4 2026: "if so allow that"); none rails USDT on Base or either on Robinhood Chain
  USDC: ['solana', 'ethereum', 'base', 'bsc'],
  USDT: ['solana', 'ethereum', 'bsc'],
  ETH: ['ethereum', 'base', 'robinhood'],
  BNB: ['bsc'],
}

export const isNetwork = (value: string | null | undefined): value is Network => (NETWORKS as readonly string[]).includes(value ?? '')
export const isExchangeCoin = (value: string | null | undefined): value is ExchangeCoin => (EXCHANGE_COINS as readonly string[]).includes(value ?? '')
export const isEvmNetwork = (network: Network) => network !== 'solana'
export const defaultNetworkOf = (coin: ExchangeCoin): Network => COIN_NETWORKS[coin][0]
/** a coin on one chain only (BNB) has nothing to pick */
export const hasNetworkChoice = (coin: ExchangeCoin) => COIN_NETWORKS[coin].length > 1

/** the exchange coin a holdings row or a picked token is, by its key; null for any other token */
export function exchangeCoinOf(token: { address: string; isNative?: boolean; chain?: string } | null | undefined): ExchangeCoin | null {
  if (!token) return null
  if (token.isNative && (token.chain === undefined || token.chain === 'solana')) return 'SOL'
  if (token.address === SOL_MINT) return 'SOL'
  if (token.address === USDC_MINT) return 'USDC'
  if (token.address === USDT_MINT) return 'USDT'
  if (token.address === ETH_ID) return 'ETH'
  if (token.address === BNB_ID) return 'BNB'
  return null
}

/** the rows the account's balances are made of: everything on Solana plus the exchanges' ETH and BNB (whose rows carry their home chain) */
export const isAccountHolding = (h: Pick<Holding, 'chain' | 'custody'>) => h.chain === 'solana' || h.custody === 'vault'

/** the logo for an exchange coin by its key, the same files the holdings use */
export const exchangeCoinLogo = (coin: ExchangeCoin) => CORE_TOKEN_LOGOS[EXCHANGE_COIN_IDS[coin]]

/** the chain's mark for the corner of a coin's logo when the coin sits on a chain other than its own (ETH on Base), the way wallets badge them; nothing on its own chain */
export function chainBadgeOf(token: { address: string; isNative?: boolean; chain?: string; network?: Network }): string | undefined {
  const coin = token.network ? exchangeCoinOf(token) : null
  return coin && token.network && token.network !== defaultNetworkOf(coin) ? NETWORK_LOGOS[token.network] : undefined
}

const SOLANA_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/

export function isAddressOnNetwork(address: string, network: Network): boolean {
  return isEvmNetwork(network) ? EVM_ADDRESS.test(address) : SOLANA_ADDRESS.test(address)
}

const TX_EXPLORERS: Record<Network, string> = {
  solana: 'https://solscan.io/tx/',
  ethereum: 'https://etherscan.io/tx/',
  base: 'https://basescan.org/tx/',
  bsc: 'https://bscscan.com/tx/',
  robinhood: 'https://robinhoodchain.blockscout.com/tx/',
}
const ADDRESS_EXPLORERS: Record<Network, string> = {
  solana: 'https://solscan.io/account/',
  ethereum: 'https://etherscan.io/address/',
  base: 'https://basescan.org/address/',
  bsc: 'https://bscscan.com/address/',
  robinhood: 'https://robinhoodchain.blockscout.com/address/',
}

export function txExplorerUrl(network: string | null | undefined, tx: string | null | undefined): string | undefined {
  if (!tx) return undefined
  return `${TX_EXPLORERS[isNetwork(network) ? network : 'solana']}${tx}`
}

export function addressExplorerUrl(network: string | null | undefined, address: string | null | undefined): string | undefined {
  if (!address) return undefined
  return `${ADDRESS_EXPLORERS[isNetwork(network) ? network : 'solana']}${address}`
}

/** a network as a place-menu id, so the place components can list chains the way they list exchanges */
export const networkPlace = (network: Network) => `net:${network}`
export const networkOfPlace = (id: string): Network | null => (id.startsWith('net:') && isNetwork(id.slice(4)) ? (id.slice(4) as Network) : null)
